import {
  MenuGroupInterface,
  MenuItemInterface,
  MenuSubItemInterface
} from '../../types';
import { AuthStoreType } from '@/store/stores/auth.store';
import {
  ChartColumnIcon,
  ChartLineIcon,
  ChartPieIcon,
  ClipboardListIcon,
  ScrollTextIcon,
  SquareUserIcon,
  StoreIcon,
  BuildingIcon,
  LogOutIcon,
  MonitorIcon,
  PlaneIcon,
  ZapIcon,
  LandmarkIcon,
  MegaphoneIcon,
  BriefcaseBusinessIcon,
  IconGameStructure
} from 'stubben-edge-unified-frontend-react-components';
import { TypeInterface } from '@/interfaces/Type.interface';
import { PermissionType } from '@/constants/types';
import type { MyWorkstreamInterface } from '@/api-client/pipeline/my-workstreams';
import { mapMyWorkstreamsToSubItems } from '../../myWorkstreamsNav';
import {
  canAccessMarketIntelligence,
  canWriteMarketIntelligence
} from '@/utils/auth/canAccessMarketIntelligence';
import { getConfiguratorPolicyManagementUrl } from '@/utils/configurator';

export const slugifyType = (name: string) =>
  name.toLowerCase().replace(/\s+/g, '-');

const pipelineNavRules: PermissionType[] = [
  'pipeline_view',
  'pipeline_access',
  'pipeline_manage'
];

const projectNavRules: PermissionType[] = ['task_view', 'task_manage'];

const myWorkstreamsNavRules: PermissionType[] = [
  ...pipelineNavRules,
  ...projectNavRules
];

const audienceNavRules: PermissionType[] = ['list_view', 'list_manage'];

export const marketIntelligenceNavRules: PermissionType[] = [
  'market_intelligence_view',
  'market_intelligence_manage'
];

export interface MarketIntelligenceNavSection extends MenuSubItemInterface {
  requiresWrite?: boolean;
}

export const marketIntelligenceSections: MarketIntelligenceNavSection[] = [
  {
    title: 'Studies',
    link: '/market-intelligence/studies',
    rules: marketIntelligenceNavRules,
    requiresWrite: true
  },
  {
    title: 'Commercial panel',
    link: '/market-intelligence/panel',
    rules: marketIntelligenceNavRules,
    requiresWrite: true
  },
  {
    title: 'Schedules',
    link: '/market-intelligence/schedules',
    rules: marketIntelligenceNavRules,
    requiresWrite: true
  },
  {
    title: 'Scraper health',
    link: '/market-intelligence/health',
    rules: marketIntelligenceNavRules,
    requiresWrite: true
  },
  // Upcoming stories (FLT-2385..2388, FLT-2392..2394, FLT-2395, FLT-2397) — keep `link` omitted until delivered
  { title: 'Page audits', rules: marketIntelligenceNavRules },
  { title: 'Journey funnel', rules: marketIntelligenceNavRules },
  { title: 'Price positioning', rules: marketIntelligenceNavRules },
  { title: 'Alerts', rules: marketIntelligenceNavRules }
];

export const getVisibleMarketIntelligenceSections = (
  user?: AuthStoreType['user']
): MenuSubItemInterface[] => {
  const canWrite = canWriteMarketIntelligence(user);
  return marketIntelligenceSections
    .filter((section) => Boolean(section.link))
    .filter((section) => !section.requiresWrite || canWrite)
    .map(({ requiresWrite: _requiresWrite, ...subItem }) => subItem);
};

export const audienceUnderstandingSections: MenuSubItemInterface[] = [
  { title: 'Brands' },
  {
    title: 'Audiences',
    link: '/audiences',
    rules: audienceNavRules
  },
  {
    title: 'Explorer',
    link: '/audiences/insights/explore',
    rules: audienceNavRules
  },
  { title: 'Target Clients' },
  { title: 'Product Interest' },
  { title: 'Marketing planner' },
  { title: 'Content' },
  { title: 'Campaigns', link: '/campaigns' },
  { title: 'Templates', link: '/templates' },
  {
    title: 'Leads',
    link: '/pipelines/leads',
    rules: pipelineNavRules
  }
];

export const getVisibleAudienceUnderstandingSections = () =>
  audienceUnderstandingSections.filter((section) => Boolean(section.link));

export type FlightDeckNavOptions = {
  myWorkstreams?: MyWorkstreamInterface[];
  isLoadingMyWorkstreams?: boolean;
};

// A user with no visible section (e.g. a client tenant before the product pages land) gets no group.
function getMarketIntelligenceNavGroup(
  user?: AuthStoreType['user']
): MenuItemInterface[] {
  if (!canAccessMarketIntelligence(user)) return [];
  const subItems = getVisibleMarketIntelligenceSections(user);
  if (subItems.length === 0) return [];
  return [
    {
      title: 'Market Intelligence',
      icon: <ChartLineIcon size={18} />,
      rules: marketIntelligenceNavRules,
      subItems
    }
  ];
}

export const getFlightDeckNavList = (
  authStore: AuthStoreType,
  companyTypes?: TypeInterface[],
  individualTypes?: TypeInterface[],
  options?: FlightDeckNavOptions
): Array<MenuGroupInterface> => [
  {
    items: [
      {
        title: 'Flightplan',
        link: '/flightplan',
        icon: <PlaneIcon size={18} />
      },
      {
        title: 'Objectives',
        link: '/objectives',
        icon: <IconGameStructure size={18} />,
        rules: ['objective_view']
      },
      {
        title: 'Dashboard',
        link: '/dashboard',
        icon: <ChartColumnIcon size={18} />
      },
      ...(options?.isLoadingMyWorkstreams
        ? [
            {
              title: 'My Work',
              rules: myWorkstreamsNavRules,
              icon: <BriefcaseBusinessIcon size={18} />,
              subItems: [],
              isLoading: true
            } as MenuItemInterface
          ]
        : options?.myWorkstreams && options.myWorkstreams.length > 0
          ? [
              {
                title: 'My Work',
                rules: myWorkstreamsNavRules,
                icon: <BriefcaseBusinessIcon size={18} />,
                subItems: mapMyWorkstreamsToSubItems(options.myWorkstreams)
              } as MenuItemInterface
            ]
          : []),
      {
        title: 'Audiences',
        icon: <MegaphoneIcon size={18} />,
        subItems: getVisibleAudienceUnderstandingSections()
      },
      {
        title: 'Pipelines',
        rules: pipelineNavRules,
        icon: <ZapIcon size={18} />,
        subItems: [
          {
            title: 'All pipelines',
            link: '/pipelines',
            rules: pipelineNavRules
          },
          {
            title: 'All deals',
            link: '/pipelines/deals',
            rules: pipelineNavRules
          },
          {
            title: 'Leaderboard',
            link: '/pipelines/leaderboard',
            rules: pipelineNavRules
          }
        ]
      },
      {
        title: 'Projects',
        rules: projectNavRules,
        icon: <ClipboardListIcon size={18} />,
        subItems: [
          {
            title: 'All projects',
            link: '/projects',
            rules: pipelineNavRules
          },
          {
            title: 'All tasks',
            link: '/projects/tasks',
            rules: projectNavRules
          }
        ]
      },
      {
        title: 'Companies',
        icon: <BuildingIcon size={18} />,
        rules: [
          'company_view',
          'company_create',
          'company_manage',
          'company_profile_edit',
          'company_profile_view',
          'company_profile_manage',
          'company_view'
        ],
        subItems: [
          {
            title: 'All',
            link: '/companies?type=all'
          },
          ...(companyTypes?.map((type) => ({
            title: type.name,
            link: `/companies?type=${slugifyType(type.name)}`
          })) || [])
        ]
      },
      {
        title: 'Individuals',
        icon: <SquareUserIcon size={18} />,
        rules: ['individual_view'],
        subItems: [
          {
            title: 'All',
            link: '/individuals?type=all'
          },
          ...(individualTypes?.map((type) => ({
            title: type.name,
            link: `/individuals?type=${slugifyType(type.name)}`
          })) || [])
        ]
      },
      {
        title: 'Marketplace',
        rules: [
          'product_view',
          'product_request',
          'product_create',
          'product_delete',
          'product_request',
          'api_view'
        ],
        subItems: [
          { title: 'Find Products', link: '/find-products' },
          {
            title: 'Find APIs',
            link: '/find-apis',
            rules: ['api_view' as const]
          },
          { title: 'My products', link: '/my-products' },
          ...(authStore.user?.company?.tenant_is_seg_parent
            ? [
                {
                  title: 'Providers',
                  link: '/providers',
                  rules: ['provider_manage' as const]
                }
              ]
            : []),
          ...(authStore.user?.company?.tenant_is_seg_parent
            ? [
                {
                  title: 'Fee structures',
                  link: '/fee-structures',
                  rules: ['fee_structure_manage' as const]
                }
              ]
            : [])
        ],
        icon: <StoreIcon size={18} />
      },
      {
        title: 'Business Ops',
        icon: <MonitorIcon size={18} />,
        subItems: [
          {
            title: 'Integration',
            link: '/integration',
            rules: ['integration_view']
          },
          {
            title: 'Document templates',
            link: '/document-templates',
            rules: ['document_template_view', 'document_template_manage']
          },
          {
            title: 'Sub-product configuration',
            link: '/sub-products',
            rules: ['sub_product_manage']
          },
          {
            title: 'Workflow management',
            link: '/workflows'
          },
          {
            title: 'Market valuation',
            link: '/market-valuation'
          }
        ]
      },
      {
        title: 'Data centre',
        icon: <ChartPieIcon size={18} />,
        subItems: [
          {
            title: 'Data playpen',
            link: '/data-playpen'
          },
          {
            title: 'Data completeness score',
            link: '/data-completeness-score'
          }
        ]
      },
      {
        title: 'Policy management',
        icon: <ScrollTextIcon size={18} />,
        rules: ['policy_view', 'off_platform_policy_view'],
        subItems: [
          {
            title: 'Online Policies',
            link: getConfiguratorPolicyManagementUrl(),
            rules: ['policy_view'],
            isExternal: true
          },
          {
            title: 'Offline Policies',
            link: '/off-platform-policies',
            rules: ['off_platform_policy_view']
          },
          {
            title: 'Cedar',
            link:
              import.meta.env.VITE_CEDAR_BROKER_PORTAL_LOGIN_URL ||
              'https://cedar.lucidtestbed.co.uk/index.php',
            isExternal: true
          }
        ]
      },
      ...(authStore.user?.canManageSavingsAccounts
        ? [
            {
              title: 'Savings management',
              icon: <LandmarkIcon size={18} className="fill-[#667085]" />,
              rules: [
                'savings_view' as PermissionType,
                'savings_manage' as PermissionType
              ],
              subItems: [
                { title: 'Home dashboard', link: '/akoni' },
                { title: 'Individuals', link: '/akoni/individuals' },
                { title: 'Customers', link: '/akoni/customers' },
                { title: 'Introducers', link: '/akoni/introducers' },
                { title: 'Applications', link: '/akoni/applications' },
                {
                  title: 'Instructions',
                  link: '/akoni/deposit-instructions'
                },
                { title: 'Accounts', link: '/akoni/deposits' },
                { title: 'Transactions', link: '/akoni/transactions' },
                {
                  title: 'Interest rates',
                  link: '/akoni/products'
                },
                { title: 'Banks', link: '/akoni/banks' },
                {
                  title: 'Direct billing',
                  link: '/akoni/direct-billing-reports'
                }
              ]
            }
          ]
        : []),
      ...getMarketIntelligenceNavGroup(authStore.user),
      {
        title: 'Log out',
        link: '/logout',
        icon: <LogOutIcon size={18} />
      }
    ]
  }
];

export const companyInfo: MenuItemInterface = {
  subItems: [
    {
      title: 'Company profile',
      link: '/company',
      rules: ['company_profile_view']
    },
    {
      title: 'Settings',
      link: '/settings',
      rules: ['company_profile_view']
    },
    {
      title: 'Membership',
      link: '/flightdeck-passport'
    },
    {
      title: 'Team',
      link: '/team',
      rules: ['team_view']
    },
    {
      title: 'Call groups',
      link: '/call-groups',
      feature: 'inbound_calling',
      rules: ['call_group_view', 'call_group_manage']
    },
    {
      title: 'Roles & permissions',
      link: '/roles',
      rules: ['role_view', 'role_create', 'role_view', 'role_manage']
    }
  ]
};
